/**
 * InnerMirrorAck — public recipient landing page.
 * Route: /inner-mirror/ack/:token  (no auth required)
 *
 * Shows the sender's first name + public card and asks the recipient to
 * confirm "Yes, I received this." / "Not me." The confirmation drives
 * the 500-credit challenge counter on the sender's account.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/hooks/useAuth";

const INDIGO = "#5D00FF";
const INK = "#1C1C1C";
const MUTE = "#676773";

export default function InnerMirrorAck() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, err: null });
  const [done, setDone] = useState(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { data } = await api.get(`/inner-mirror/ack/${token}`);
        if (!dead) setState({ loading: false, data, err: null });
      } catch (e) {
        if (!dead) setState({
          loading: false, data: null,
          err: e?.response?.status === 410 ? "expired" : "not_found",
        });
      }
    })();
    return () => { dead = true; };
  }, [token]);

  const confirm = async (accepted) => {
    try {
      const { data } = await api.post(`/inner-mirror/ack/${token}/confirm`,
                                        { accepted });
      setDone(data?.duplicate ? "duplicate" : accepted ? "accepted" : "declined");
    } catch (_) { setDone("error"); }
  };

  return (
    <div className="ack-shell">
      <div className="ack-top">
        <div className="ack-brand">KALKI</div>
        <div className="ack-sub">THE INNER MIRROR 🪞</div>
      </div>
      <main className="ack-main">
        {state.loading && <p className="ack-mute">Loading your invitation…</p>}
        {state.err === "expired" && (
          <>
            <h1 className="ack-h1">This invitation has expired.</h1>
            <p className="ack-mute">Ask the sender to reshare it.</p>
            <Link className="ack-primary" to="/">Explore KALKI →</Link>
          </>
        )}
        {state.err === "not_found" && (
          <>
            <h1 className="ack-h1">We couldn't find this invitation.</h1>
            <Link className="ack-primary" to="/">Explore KALKI →</Link>
          </>
        )}
        {state.data && !done && (
          <>
            <h1 className="ack-h1">Someone shared a little light with you.</h1>
            <p className="ack-mute">
              <strong>{state.data.sender_first_name}</strong> wanted to share
              their KALKI experience.
            </p>
            <div className="ack-card">
              <div className="ack-badge">
                {state.data.badge === "founding_devotee"
                  ? `KALKI · Founding Devotee · First 110${
                      state.data.founding_rank ? ` · #${state.data.founding_rank}` : ""}`
                  : "KALKI · Devotee"}
              </div>
              <div className={`ack-card-ring${state.data.avatar_url ? " has-dp" : ""}`}
                   data-testid="ack-card-ring">
                {state.data.avatar_url && (
                  <img src={state.data.avatar_url}
                       alt={`${state.data.sender_first_name}'s photo`}
                       data-testid="ack-avatar" />
                )}
              </div>
              <div className="ack-sender">{state.data.sender_first_name}</div>
              <div className="ack-tag">
                "A better world begins with a better intention."
              </div>
            </div>
            {state.data.already_acknowledged ? (
              <p className="ack-mute" data-testid="ack-already">
                You already acknowledged this invitation. Thank you.
              </p>
            ) : (
              <>
                <p className="ack-q">Did you receive this personally from {state.data.sender_first_name}?</p>
                <div className="ack-row">
                  <button className="ack-primary" data-testid="ack-yes"
                          onClick={() => confirm(true)}>
                    Yes, I Received It ❤️
                  </button>
                  <button className="ack-ghost" data-testid="ack-no"
                          onClick={() => confirm(false)}>Not Me</button>
                </div>
              </>
            )}
          </>
        )}
        {done === "accepted" && (
          <>
            <h1 className="ack-h1">Thank you.</h1>
            <p className="ack-mute">May your journey be filled with clarity and kindness.</p>
            <Link className="ack-primary" to="/">Explore KALKI →</Link>
          </>
        )}
        {done === "declined" && (
          <>
            <h1 className="ack-h1">No problem.</h1>
            <p className="ack-mute">If you'd still like to explore KALKI, you're welcome.</p>
            <Link className="ack-primary" to="/">Explore KALKI →</Link>
          </>
        )}
        {done === "duplicate" && (
          <>
            <h1 className="ack-h1">We've recorded your reply.</h1>
            <p className="ack-mute">Thank you for responding.</p>
            <Link className="ack-primary" to="/">Explore KALKI →</Link>
          </>
        )}
      </main>
      <style>{`
        .ack-shell { min-height: 100dvh; background: #FFFFFF;
                     background-image: radial-gradient(rgba(93,0,255,0.05) 1px, transparent 1px);
                     background-size: 24px 24px; color: ${INK};
                     padding: 36px 20px 60px; }
        .ack-top { max-width: 540px; margin: 0 auto 24px; text-align: center; }
        .ack-brand { font-size: 10px; letter-spacing: 0.4em; color: ${INDIGO}; font-weight: 500; }
        .ack-sub { font-family: 'EB Garamond', serif; font-size: 16px; margin-top: 4px; }
        .ack-main { max-width: 520px; margin: 0 auto; text-align: center; min-width: 0; }
        .ack-h1 { font-family: 'EB Garamond', serif; font-size: clamp(22px, 4.2vw, 28px);
                   font-weight: 500; margin: 10px 0 14px; line-height: 1.3; }
        .ack-mute { color: ${MUTE}; font-size: 14px; line-height: 1.6; }
        .ack-card { margin: 28px auto; padding: 28px 18px; max-width: 360px;
                     background: var(--kalki-snippet-surface);
                     border: 1px solid var(--kalki-snippet-border);
                     border-radius: 20px; min-width: 0; }
        .ack-badge { font-size: 10px; letter-spacing: 0.22em; color: ${INDIGO}; }
        .ack-card-ring { width: 140px; height: 140px; border-radius: 50%;
                          border: 2px solid ${INDIGO}; margin: 16px auto;
                          box-shadow: 0 0 0 6px rgba(0,194,203,0.14);
                          overflow: hidden; display: flex;
                          align-items: center; justify-content: center;
                          background: #FFFFFF; }
        .ack-card-ring img { width: 100%; height: 100%;
                              object-fit: cover; display: block; }
        .ack-sender { font-family: 'EB Garamond', serif; font-size: 24px;
                       color: ${INK}; overflow-wrap: anywhere; min-width: 0; }
        .ack-tag { font-style: italic; color: ${MUTE}; font-size: 12.5px;
                    margin-top: 10px; }
        .ack-q { margin-top: 20px; font-size: 15px; color: ${INK}; }
        .ack-row { display: flex; gap: 10px; justify-content: center;
                    flex-wrap: wrap; margin-top: 16px; }
        .ack-primary { display: inline-flex; align-items: center; gap: 6px;
                        background: linear-gradient(135deg, ${INDIGO}, #FF2E9A);
                        color: #FFF; text-decoration: none;
                        padding: 12px 22px; border-radius: 999px;
                        font-size: 12px; letter-spacing: 0.14em;
                        font-weight: 600; border: 0; cursor: pointer; }
        .ack-ghost { background: transparent; border: 1px solid rgba(28,28,28,0.14);
                      color: ${INK}; padding: 11px 20px; border-radius: 999px;
                      font-size: 12px; letter-spacing: 0.1em; cursor: pointer; }
      `}</style>
    </div>
  );
}
